import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UNITE_STATUTS, UNITE_CATEGORIES } from "../lib/format";
import { Building2, Ruler, Tag, ArrowLeft, ArrowRight, CheckCircle2, Home, BedDouble, Bath, Sofa } from "lucide-react";
import api from "../api/client";
import { toast } from "sonner";

const TYPES = [
  { value: "appartement", label: "Appartement", icon: Building2 },
  { value: "studio", label: "Studio", icon: Sofa },
  { value: "duplex", label: "Duplex", icon: Home },
  { value: "maison", label: "Maison", icon: Home },
];

const CATEGORIES = [
  { value: "", label: "Standard", desc: "Finitions classiques" },
  { value: "confort", label: "Confort", desc: "Bonne gamme" },
  { value: "premium", label: "Premium", desc: "Haut de gamme" },
  { value: "luxe", label: "Luxe", desc: "Très haut de gamme" },
];

/**
 * Wizard 3 étapes pour créer une unité.
 * Props : open, onClose, immeubleId, onCreated()
 */
export default function UniteWizard({ open, onClose, immeubleId, onCreated }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initial());
  const [suggestedNom, setSuggestedNom] = useState("");

  function initial() {
    return {
      type_unite: "appartement",
      etage: 0,
      categorie: "",
      nom: "",
      surface_m2: "",
      nb_chambres: "",
      nb_salles_bain: "",
      prix_vente: "",
      prix_location: "",
      statut: "disponible",
      description: "",
    };
  }

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm(initial());
    }
  }, [open]);

  // Suggest nom when type changes
  useEffect(() => {
    if (!immeubleId || !form.type_unite) return;
    api.get("/unites/next-numero", { params: { immeuble_id: immeubleId, type_unite: form.type_unite } })
      .then(({ data }) => {
        setSuggestedNom(data?.nom || "");
        setForm((f) => ({ ...f, nom: data?.nom || f.nom }));
      })
      .catch(() => {});
  }, [immeubleId, form.type_unite]);

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const canNext1 = !!form.type_unite;
  const canNext2 = !!form.nom;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        immeuble_id: immeubleId,
        nom: form.nom,
        etage: Number(form.etage) || 0,
        type_unite: form.type_unite,
        categorie: form.categorie || null,
        surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
        nb_chambres: form.nb_chambres ? Number(form.nb_chambres) : null,
        nb_salles_bain: form.nb_salles_bain ? Number(form.nb_salles_bain) : null,
        prix_vente: form.prix_vente ? Number(form.prix_vente) : null,
        prix_location: form.prix_location ? Number(form.prix_location) : null,
        statut: form.statut,
        description: form.description || "",
      };
      await api.post("/unites", payload);
      toast.success("Unité créée");
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de création");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden" data-testid="unite-wizard">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-[#E2E5F1]">
          <DialogTitle className="font-display text-2xl text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Nouvelle unité
          </DialogTitle>
          {/* Stepper */}
          <div className="flex items-center gap-2 mt-4">
            {[
              { n: 1, label: "Type & étage", icon: Building2 },
              { n: 2, label: "Détails", icon: Ruler },
              { n: 3, label: "Prix & statut", icon: Tag },
            ].map((s, i) => {
              const Ic = s.icon;
              const active = step === s.n;
              const done = step > s.n;
              return (
                <React.Fragment key={s.n}>
                  <div className={`flex items-center gap-2 px-2 py-1 rounded-md ${active ? "bg-[#E8EBFA]" : ""}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-[#2D7A3F] text-white" : active ? "bg-[#1E3A8A] text-white" : "bg-[#F1F3FB] text-[#9CA3B5]"}`}>
                      {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
                    </div>
                    <div className={`text-[11px] uppercase tracking-wider font-bold ${active ? "text-[#1E3A8A]" : done ? "text-[#2D7A3F]" : "text-[#9CA3B5]"}`}>
                      {s.label}
                    </div>
                  </div>
                  {i < 2 && <div className={`flex-1 h-0.5 ${done ? "bg-[#2D7A3F]" : "bg-[#E2E5F1]"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </DialogHeader>

        <div className="px-7 py-6 max-h-[65vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-2 block">Type d'unité</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TYPES.map((t) => {
                    const Ic = t.icon;
                    const sel = form.type_unite === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm({ ...form, type_unite: t.value })}
                        className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition ${sel ? "border-[#1E3A8A] bg-[#E8EBFA]" : "border-[#E2E5F1] hover:border-[#1E3A8A]/40"}`}
                        data-testid={`type-${t.value}`}
                      >
                        <Ic className={`w-7 h-7 ${sel ? "text-[#1E3A8A]" : "text-[#4B5775]"}`} strokeWidth={1.5} />
                        <span className={`text-sm font-bold ${sel ? "text-[#1E3A8A]" : "text-[#0F1A3D]"}`}>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Étage</Label>
                  <Input type="number" value={form.etage} onChange={(e) => setForm({ ...form, etage: e.target.value })} placeholder="0 = RDC" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Catégorie</Label>
                  <Select value={form.categorie || "standard"} onValueChange={(v) => setForm({ ...form, categorie: v === "standard" ? "" : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.value || "standard"} value={c.value || "standard"}>{c.label} — <span className="text-[#9CA3B5]">{c.desc}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Nom *</Label>
                <Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder={suggestedNom || "Appartement 1"} data-testid="wizard-nom" />
                {suggestedNom && form.nom !== suggestedNom && (
                  <button type="button" onClick={() => setForm({ ...form, nom: suggestedNom })} className="text-[11px] text-[#E91E63] hover:underline mt-1">
                    Utiliser la suggestion : {suggestedNom}
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Surface (m²)</Label>
                  <div className="relative">
                    <Ruler className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
                    <Input type="number" className="pl-9" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} placeholder="65" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Chambres</Label>
                  <div className="relative">
                    <BedDouble className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
                    <Input type="number" className="pl-9" value={form.nb_chambres} onChange={(e) => setForm({ ...form, nb_chambres: e.target.value })} placeholder="3" />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Salles de bain</Label>
                  <div className="relative">
                    <Bath className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
                    <Input type="number" className="pl-9" value={form.nb_salles_bain} onChange={(e) => setForm({ ...form, nb_salles_bain: e.target.value })} placeholder="2" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Description (optionnel)</Label>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Bel appartement avec balcon, vue dégagée, cuisine équipée..."
                  className="w-full px-3 py-2 text-sm rounded-md border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>
              <div className="bg-[#F8FAFC] border border-[#E2E5F1] rounded-lg p-3 text-xs text-[#4B5775]">
                <div className="font-semibold text-[#0F1A3D] mb-1">Récapitulatif</div>
                <span className="font-bold">{form.nom}</span> · {TYPES.find(t => t.value === form.type_unite)?.label} · Étage {form.etage}
                {form.categorie && <> · <span className="capitalize">{form.categorie}</span></>}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Prix de vente (FCFA)</Label>
                  <Input type="number" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} placeholder="50000000" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Loyer mensuel (FCFA)</Label>
                  <Input type="number" value={form.prix_location} onChange={(e) => setForm({ ...form, prix_location: e.target.value })} placeholder="350000" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#4B5775] font-bold mb-1 block">Statut initial</Label>
                <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(UNITE_STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Récap final */}
              <div className="bg-gradient-to-br from-[#0F1A3D] to-[#1E3A8A] text-white rounded-xl p-5 mt-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold mb-2">À enregistrer</div>
                <div className="font-display text-xl font-extrabold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{form.nom}</div>
                <div className="text-xs text-white/80 mt-1">
                  {TYPES.find(t => t.value === form.type_unite)?.label} · Étage {form.etage}
                  {form.categorie && <> · {CATEGORIES.find(c => c.value === form.categorie)?.label}</>}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[11px] text-white/70">
                  {form.surface_m2 && <span>{form.surface_m2} m²</span>}
                  {form.nb_chambres && <span>{form.nb_chambres} ch.</span>}
                  {form.nb_salles_bain && <span>{form.nb_salles_bain} SDB</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-7 py-4 border-t border-[#E2E5F1] flex items-center justify-between bg-[#F8FAFC]">
          <button
            type="button"
            onClick={step === 1 ? onClose : prev}
            className="h-10 px-4 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] text-[#4B5775] hover:text-[#1E3A8A] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
            data-testid="wizard-prev"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {step === 1 ? "Annuler" : "Retour"}
          </button>
          <div className="text-xs text-[#9CA3B5] font-bold">Étape {step}/3</div>
          {step < 3 ? (
            <button
              type="button"
              onClick={next}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              className="h-10 px-5 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="wizard-next"
            >
              Suivant <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={saving || !form.nom}
              className="h-10 px-5 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="wizard-submit"
            >
              {saving ? "..." : <>Créer l'unité <CheckCircle2 className="w-3.5 h-3.5" /></>}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
